import { useCallback, useEffect, useRef, useState } from "react";
import { Range } from 'react-date-range';
import { EyeIcon, FilterIcon2, VendingActiveIcon } from "../../../../assets/icons";
import Button from "../../../../common/components/xepp/button/Button";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import styles from "../bills.module.scss";
import { Transaction, TransactionChannelMap, TransactionStatusMap, VendingStatusMap, vendingStatusClassName } from "../../../../model/transaction";
import MapRefundTokenCell from "../../../../common/components/xepp/map-refund-token-cell/MapRefundTokenCell";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import TransactionDetails from "../../../../common/components/xepp/transaction-details/TransactionDetails";
import { Utility } from "../../../../model/bill-settings/bill-settings";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import { useLazyGetPrepaidTransactionsQuery, useLazyExportPrepaidTransactionsQuery } from "../../../../service/transaction-service";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import { formatCurrency, formatTimestamp } from "../../../../utils";
import Filter from "../../../../common/components/xepp/table-filter/table-filter-components";
import { FilterItem } from "../../../../common/components/xepp/table-filter/table-filter.type";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../../utils/error-handler";
import { useReVendMutation } from "../../../../service/vending-service";

interface VendingPageHistoryProps {
	selectedUtility: Utility;
}

export const VendingPageHistory = ({ selectedUtility }: VendingPageHistoryProps) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();
	const [Page, setPage] = useState(1);
	const PageSize = 15;

	// Filter states
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [selectedTransactionStatus, setSelectedTransactionStatus] = useState<string>("");
	const [selectedVendingStatus, setSelectedVendingStatus] = useState<string>("");
	const [selectedMapRefundToken, setSelectedMapRefundToken] = useState<string>("");
	const [dateRange, setDateRange] = useState<Range>({
		startDate: undefined,
		endDate: undefined,
		key: 'selection'
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		TransactionStatus: string;
		VendingStatus: string;
		HasMapRefundToken: string;
		From?: string;
		To?: string;
	}>({
		TransactionStatus: "",
		VendingStatus: "",
		HasMapRefundToken: "",
		From: undefined,
		To: undefined,
	});

	const [getPrepaidTransactions, { data, isLoading, isFetching }] = useLazyGetPrepaidTransactionsQuery();
	const [Revend, { isLoading: reVending }] = useReVendMutation();
	const [exportPrepaidTransactions] = useLazyExportPrepaidTransactionsQuery();
	const { updateLayout } = useConfigLayout();

	const appliedFiltersRef = useRef(appliedFilters);
	const searchTermRef = useRef(debouncedSearchTerm);
	useEffect(() => {
		appliedFiltersRef.current = appliedFilters;
	}, [appliedFilters]);
	useEffect(() => {
		searchTermRef.current = debouncedSearchTerm;
	}, [debouncedSearchTerm]);

	const { count } = data ?? {};

	useEffect(() => {
		if (selectedUtility) {
			const filterParams = {
				Page,
				PageSize,
				UtilityId: selectedUtility.id,
				...(appliedFilters.TransactionStatus && { TransactionStatus: appliedFilters.TransactionStatus }),
				...(appliedFilters.VendingStatus && { VendingStatus: appliedFilters.VendingStatus }),
				...(appliedFilters.HasMapRefundToken !== "" && { HasMapRefundToken: appliedFilters.HasMapRefundToken === "true" }),
				...(appliedFilters.From && { DateFrom: appliedFilters.From }),
				...(appliedFilters.To && { DateTo: appliedFilters.To }),
				...(debouncedSearchTerm && { Reference: debouncedSearchTerm }),
			};

			getPrepaidTransactions(filterParams).unwrap()
				.catch(err => getErrorMessage(err.data))
		}
	}, [selectedUtility, Page, PageSize, appliedFilters, debouncedSearchTerm, getPrepaidTransactions]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleExport = useCallback(() => {
		const filters = appliedFiltersRef.current;
		const search = searchTermRef.current;
		const exportParams = {
			Page: 1,
			PageSize: 0,
			UtilityId: selectedUtility.id,
			...(filters.TransactionStatus && { TransactionStatus: filters.TransactionStatus }),
			...(filters.VendingStatus && { VendingStatus: filters.VendingStatus }),
			...(filters.HasMapRefundToken !== "" && { HasMapRefundToken: filters.HasMapRefundToken === "true" }),
			...(filters.From && { DateFrom: filters.From }),
			...(filters.To && { DateTo: filters.To }),
			...(search && { Reference: search }),
		};
		toast.info("Preparing export...");
		exportPrepaidTransactions(exportParams)
			.unwrap()
			.then(blob => {
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `prepaid-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			})
			.catch((err: any) => {
				toast.error(getErrorMessage(err?.data));
			});
	}, [exportPrepaidTransactions, selectedUtility.id]);

	useEffect(() => {
		updateLayout({ exportHandler: handleExport });
		return () => {
			updateLayout({ exportHandler: null });
		};
	}, [handleExport, updateLayout]);

	const handleViewTransaction = (transaction: Transaction) => {
		setIsOpen(true);
		setSelectedTransaction(transaction);
	};

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleApplyFilter = () => {
		setAppliedFilters({
			TransactionStatus: selectedTransactionStatus,
			VendingStatus: selectedVendingStatus,
			HasMapRefundToken: selectedMapRefundToken,
			From: dateRange.startDate ? dateRange.startDate.toISOString() : undefined,
			To: dateRange.endDate ? dateRange.endDate.toISOString() : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setSelectedTransactionStatus("");
		setSelectedVendingStatus("");
		setSelectedMapRefundToken("");
		setIsFilterOpen(false);

		setDateRange({
			startDate: new Date(),
			endDate: new Date(),
			key: 'selection'
		});

		setAppliedFilters({
			TransactionStatus: "",
			VendingStatus: "",
			HasMapRefundToken: "",
			From: undefined,
			To: undefined,
		});

		setPage(1);
	};

	const handleTransactionStatusChange = (value: string) => {
		setSelectedTransactionStatus(value);
	};

	const handleVendingStatusChange = (value: string) => {
		setSelectedVendingStatus(value);
	};

	const handleMapRefundTokenChange = (value: string) => {
		setSelectedMapRefundToken(value);
	};

	const handleDateRangeChange = (range: Range) => {
		setDateRange(range);
	};

	const handleRevend = () => {
		let vendingDetailsId = selectedTransaction?.vendingDetails?.id ?? ""
		Revend({ vendingDetailsId }).unwrap()
			.then(res => {
				toast.success('Vending Successful')
			}).catch(err => {
				toast.error(getErrorMessage(err?.data))
			})
	}

	const filterItems: FilterItem[] = [
		{
			id: "transaction_status",
			label: "PAYMENT STATUS",
			type: "radio",
			options: [
				{
					id: "completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedTransactionStatus === "Completed",
				},
				{
					id: "failed",
					label: "Failed",
					value: "Failed",
					isSelected: selectedTransactionStatus === "Failed",
				},
				{
					id: "in-progress",
					label: "In-Progress",
					value: "InProgress",
					isSelected: selectedTransactionStatus === "InProgress",
				},
			],
			onChange: handleTransactionStatusChange,
		},
		{
			id: "vending_status",
			label: "VENDING STATUS",
			type: "radio",
			options: [
				{
					id: "vending_completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedVendingStatus === "Completed",
				},
				{
					id: "vending_failed",
					label: "Failed",
					value: "Failed",
					isSelected: selectedVendingStatus === "Failed",
				},
				{
					id: "vending_in-progress",
					label: "In-Progress",
					value: "InProgress",
					isSelected: selectedVendingStatus === "InProgress",
				},
				{
					id: "vending_failed_with_refund",
					label: "Failed with Refund",
					value: "FailedWithRefund",
					isSelected: selectedVendingStatus === "FailedWithRefund",
				},
			],
			onChange: handleVendingStatusChange,
		},
		{
			id: "map_refund_token",
			label: "MAP REFUND TOKEN",
			type: "radio",
			options: [
				{
					id: "map_refund_with",
					label: "With MAP Refund Token",
					value: "true",
					isSelected: selectedMapRefundToken === "true",
				},
				{
					id: "map_refund_without",
					label: "Without MAP Refund Token",
					value: "false",
					isSelected: selectedMapRefundToken === "false",
				},
			],
			onChange: handleMapRefundTokenChange,
		},
		// {
		// 	id: "date_range",
		// 	label: "DATE RANGE",
		// 	type: "date",
		// 	initialDateRange: dateRange,
		// 	onChange: handleDateRangeChange,
		// },
	];

	const columns: Array<ColumnType<Transaction>> = [
		{
			accessor: "user",
			header: "Name of Customer",
			render: (record: Transaction) => (
				<span>
					{record.user.firstName} {record.user.lastName}
				</span>
			),
		},
		{
			accessor: "reference",
			header: "Reference",
			render: (record: Transaction) => (
				<span title={record.reference} className={styles.ellipsisText}>
					{record.reference}
				</span>
			),
		},
		{
			accessor: "amount.amount",
			header: "Amount Paid",
			render: (record: Transaction) => <span>{record.amount.amount ? formatCurrency(record.amount.amount, { currency: record.amount.currency }) : "N/A"}</span>,
		},
		{
			accessor: "type",
			header: "Channel",
			render: (record: Transaction) => <span className={styles.channelBadge}>{TransactionChannelMap[record.type]}</span>,
		},
		{
			accessor: "status",
			header: "Payment Status",
			render: (record: Transaction) => (
				<span className={`${styles.statusIndicator} ${record.status ? styles[TransactionStatusMap[record.status].toLowerCase()] : ""}`}>
					{TransactionStatusMap[record.status]}
				</span>
			),
		},
		{
			accessor: "vendingDetails.vendingToken.vendingStatus",
			header: "Vending Status",
			render: (record: Transaction) => {
				const vendingStatus = record.vendingDetails?.vendingToken?.vendingStatus ?? 0;
				return (
					<span className={`${styles.statusIndicator} ${styles[vendingStatusClassName(vendingStatus)] ?? ""}`}>
						{VendingStatusMap[vendingStatus] ?? "Unknown"}
					</span>
				);
			},
		},
		{
			accessor: "vendingDetails.vendingToken.mapRefundToken",
			header: "MAP Refund Token",
			render: (record: Transaction) => <MapRefundTokenCell token={record.vendingDetails?.vendingToken?.mapRefundToken} />,
		},
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: Transaction) => <span>{formatTimestamp(record.createdAt ?? "")}</span>,
		},
		{
			accessor: "id",
			header: "",
			render: (record: Transaction) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => handleViewTransaction(record)} />
			),
		},
	];

	// Header actions component
	const headerActions = (
		<>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search by reference..." />
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	return (
		<div className={styles.historyContainer}>
			<Filter filterItems={filterItems} onApplyFilter={handleApplyFilter} onResetFilter={handleResetFilter} onClose={handleCloseFilter} isOpen={isFilterOpen} />
			{isOpen && (
				<CommonSideDialog onCloseDialog={() => setIsOpen(!isOpen)}>
					<TransactionDetails
						transactionType="vending"
						transaction={{
							date: selectedTransaction?.createdAt ?? "",
							time: selectedTransaction?.createdAt ?? "",
							customerName: `${selectedTransaction?.user.firstName} ${selectedTransaction?.user.lastName}`,
							amount: selectedTransaction?.amount?.amount
								? formatCurrency(selectedTransaction?.amount?.amount, { currency: selectedTransaction?.amount.currency })
								: "N/A",
							paymentStatus: selectedTransaction?.status ?? 0,
							transactionId: selectedTransaction?.externalReference ?? "",
							unitBought: selectedTransaction?.vendingDetails?.vendingToken?.totalKwh.toString() ?? "0",
							paymentChannel: selectedTransaction?.type ?? 0,
							token: selectedTransaction?.vendingDetails?.vendingToken?.token ?? "",
							mapRefundToken: selectedTransaction?.vendingDetails?.vendingToken?.mapRefundToken,
							vendingStatus: selectedTransaction?.vendingDetails?.vendingToken?.vendingStatus,
							reference: selectedTransaction?.reference ?? "",
						}}
						onRevend={handleRevend}
						reVendLoading={reVending}
						onClose={() => setIsOpen(!isOpen)}
					/>
				</CommonSideDialog>
			)}
			<DataTable<Transaction>
				data={data?.items ?? []}
				loading={isLoading}
				isUpdating={isFetching}
				columns={columns}
				headerActions={headerActions}
				emptyComponent={<EmptyState icon={<VendingActiveIcon />} title="No vending history data" />}
			/>
			<TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={PageSize} currentPage={Page} onPageChange={setPage} />
		</div>
	);
};
